import React from 'react';
import { Button } from '@uhg-abyss/web/ui/Button';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { Card } from '@uhg-abyss/web/ui/Card';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Skeleton } from '@uhg-abyss/web/ui/Skeleton';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { useLocation } from '@uhg-abyss/web/tools/reactRouterTools/reactRouterDom';
import { event as TaggingEvent } from '@uhg-abyss/web/tools/event';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { SecondaryServicing } from './SecondaryServicing/SecondaryServicing';

const ServicingValue = styled('div', {
  color: 'rgb(51, 51, 51)',
  fontSize: '16px',
  lineHeight: '22px',
  fontWeight: 700,
  fontStyle: 'normal',
  marginTop: '2.3%',
  overflowWrap: 'break-word',
  pageBreakInside: 'avoid',
});
const BillingValue = styled('div', {
  color: 'rgb(51, 51, 51)',
  fontSize: '16px',
  lineHeight: '22px',
  fontWeight: 700,
  fontStyle: 'normal',
  marginTop: '2.3%',
  overflowWrap: 'break-word',
  pageBreakInside: 'avoid',
  marginBottom: '30px',
});

const Name = styled('div', {
  color: '$gray8',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '22px',
  marginBottom: '4px',
});

export const Locations = () => {
  const router = useRouter();
  const form = useForm();
  const location = useLocation();
  const isFinalStep = location.pathname.includes('/step/4');
  const address = form.getValues('address');

  const fileNameStyle = {
    fontWeight: 600,
    color: isFinalStep ? 'rgb(25, 110, 207)' : 'rgb(51,51,51)',
    wordWrap: 'break-word',
  };

  const adobeTagging = () => {
    const interactionName = 'locations widget';
    const interactionValue = 'edit location details';
    TaggingEvent('ATTESTATION_PAGE_EDIT', {
      interactionName,
      interactionValue,
    });
  };

  const skeleton = (data, contentRenderer) => {
    return address ? (
      contentRenderer(data)
    ) : (
      <Skeleton variant="rounded" height={20} width={200}>
        {contentRenderer('-')}
      </Skeleton>
    );
  };

  const jumpLinks = (navigateTo) => {
    adobeTagging();
    const eventData = {
      action: 'EditDetails',
    };
    TaggingEvent('EAPM_TRANSACTION_CONTINUE', eventData);
    router.navigate(navigateTo);
  };

  return (
    <Card
      header={
        <div
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          Locations
          {isFinalStep && (
            <div id="locations-edit">
              <Button
                before={
                  <IconSymbol
                    icon="edit"
                    size="20px"
                    css={{ marginRight: '-10px' }}
                  />
                }
                id="attestation-locations-edit-details"
                onClick={() => {
                  return jumpLinks('/tin-registration/step/3');
                }}
                variant="tertiary"
                css={{ padding: '0px' }}
              >
                Edit Details
              </Button>
            </div>
          )}
        </div>
      }
    >
      <Card.Section>
        <Grid>
          <Grid.Col span="30%">
            <div>
              <Name id="attestation-primary-servicing-address">
                Primary Servicing Address
              </Name>
              <ServicingValue id="attestation-primary-servicing-address-value">
                {skeleton(address && address.primaryServicing, (data) => {
                  return (
                    <div>
                      <div style={{ marginBottom: '-22px' }}>
                        <span
                          id="address-primary-servicing-street-line-1"
                          style={{ marginBottom: '1px' }}
                        >
                          {data ? data.streetLine1 : '-'}
                          <br />
                        </span>
                        {data && data.streetLine2 && (
                          <div
                            id="address-primary-servicing-street-line-2"
                            style={{ marginBottom: '-22px' }}
                          >
                            {data.streetLine2}
                          </div>
                        )}
                      </div>
                      <br />
                      <div
                        style={{
                          display: 'inline-block',
                          overflowWrap: 'break-word',
                          width: '100%',
                        }}
                      >
                        <span
                          id="address-primary-servicing-city"
                          style={{
                            display: 'inline-block',
                            marginBottom: '1px',
                          }}
                        >
                          {data ? data.city : '-'}
                        </span>
                        {', '}
                        <div
                          id="address-primary-servicing-state"
                          style={{ display: 'inline-block' }}
                        >
                          {data ? data.state : '-'}
                        </div>
                        <div
                          id="address-primary-servicing-zip-code"
                          style={{ display: 'inline-block', marginLeft: '4px' }}
                        >
                          {data ? data.zipCode : '-'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </ServicingValue>
            </div>
          </Grid.Col>
          <Grid.Col span="30%">
            <div>
              <Name id="attestation-primary-billing-address">
                Primary Billing Address
              </Name>
              <BillingValue id="attestation-primary-billing-address-value">
                {skeleton(address && address.primaryBilling, (data) => {
                  return (
                    <React.Fragment>
                      <div style={{ marginBottom: '-22px' }}>
                        <p
                          id="address-primary-billing-street-line-1"
                          style={{ marginBottom: '1px' }}
                        >
                          {data ? data.streetLine1 : '-'}
                          <br />
                        </p>
                        {data && data.streetLine2 && (
                          <div
                            id="address-primary-billing-street-line-2"
                            style={{ marginBottom: '-22px' }}
                          >
                            {data.streetLine2}
                            <br />
                          </div>
                        )}
                        <br />
                      </div>
                      <br />
                      <div
                        style={{
                          display: 'inline-block',
                          overflowWrap: 'break-word',
                          width: '100%',
                        }}
                      >
                        <p
                          id="address-primary-billing-city"
                          style={{
                            display: 'inline-block',
                            marginBottom: '1px',
                          }}
                        >
                          {data ? data.city : '-'}
                        </p>
                        {', '}
                        <div
                          id="address-primary-billing-state"
                          style={{ display: 'inline-block' }}
                        >
                          {data ? data.state : '-'}
                        </div>
                        <div
                          id="address-primary-billing-zip-code"
                          style={{ display: 'inline-block', marginLeft: '4px' }}
                        >
                          {data ? data.zipCode : '-'}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </BillingValue>
            </div>
          </Grid.Col>
          <Grid.Col span="30%">
            <div>
              <Name id="attestation-primary-remittance-address">
                Primary Remittance Address
              </Name>
              <BillingValue id="attestation-primary-remittance-address-value">
                {skeleton(address && address.primaryRemittance, (data) => {
                  return (
                    <div style={{ marginBottom: '-22px' }}>
                      <p
                        id="address-primary-remittance-street-line-1"
                        style={{ marginBottom: '1px' }}
                      >
                        {data ? data.streetLine1 : '-'}

                        <br />
                      </p>
                      {data && data.streetLine2 && (
                        <div
                          id="address-primary-remittance-street-line-2"
                          style={{ marginBottom: '-22px' }}
                        >
                          {data.streetLine2} <br />
                        </div>
                      )}
                      <br />
                      <div
                        style={{
                          display: 'inline-block',
                          overflowWrap: 'break-word',
                          width: '100%',
                        }}
                      >
                        <p
                          id="address-primary-remittance-city"
                          style={{
                            display: 'inline-block',
                            marginBottom: '1px',
                          }}
                        >
                          {data ? data.city : '-'}
                        </p>
                        {', '}
                        <div
                          id="address-primary-remittance-state"
                          style={{ display: 'inline-block' }}
                        >
                          {data ? data.state : '-'}
                        </div>
                        <div
                          id="address-primary-remittance-zip-code"
                          style={{ display: 'inline-block', marginLeft: '4px' }}
                        >
                          {data ? data.zipCode : '-'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </BillingValue>
            </div>
          </Grid.Col>
        </Grid>
        <Grid>
          <Grid.Col
            span="30%"
            css={{
              '&.abyss-grid-col': {
                marginBottom: '20px',
              },
            }}
          >
            <div>
              <Name id="attestation-servicing-address-proof">
                Servicing Address Proof
              </Name>
              <div
                style={fileNameStyle}
                id="attestation-servicing-address-proof-file"
              >
                {skeleton(
                  address &&
                    address.primaryServicing &&
                    address.primaryServicingProof,
                  (data) => {
                    return <div>{data ? data.name : '-'}</div>;
                  }
                )}
              </div>
            </div>
          </Grid.Col>
          <Grid.Col span="30%">
            <div>
              <Name id="attestation-billig-address-proof">
                Billing Address Proof
              </Name>
              <div
                style={fileNameStyle}
                id="attestation-billig-address-proof-file"
              >
                {skeleton(
                  address &&
                    address.primaryBilling &&
                    address.primaryBillingProof,
                  (data) => {
                    return <div>{data ? data.name : '-'}</div>;
                  }
                )}
              </div>
            </div>
          </Grid.Col>
          <Grid.Col span="30%">
            <div>
              <Name id="attestation-remittance-address-proof">
                Remittance Address Proof
              </Name>
              <div
                style={fileNameStyle}
                id="attestation-remittance-address-proof-file"
              >
                {skeleton(
                  address &&
                    address.primaryRemittance &&
                    address.primaryRemittanceProof,
                  (data) => {
                    return <div>{data ? data.name : '-'}</div>;
                  }
                )}
              </div>
            </div>
          </Grid.Col>
        </Grid>
        <Grid>
          {address &&
            address.secondaryServicing &&
            address.secondaryServicing[0] && (
              <Grid.Col
                span="30%"
                css={{
                  '&.abyss-grid-col': {
                    marginBottom: '20px',
                    marginTop: '-35px',
                  },
                }}
              >
                <div>
                  <br />
                  <Name>Secondary Servicing Address</Name>
                  <SecondaryServicing index={0} address={address} />
                </div>
              </Grid.Col>
            )}
          {address &&
            address.secondaryServicing &&
            address.secondaryServicing[1] && (
              <Grid.Col
                span="30%"
                css={{
                  '&.abyss-grid-col': {
                    marginBottom: '20px',
                    marginTop: '-35px',
                  },
                }}
              >
                <div>
                  <br />
                  <br />
                  <SecondaryServicing index={1} address={address} />
                </div>
              </Grid.Col>
            )}

          {address &&
            address.secondaryServicing &&
            address.secondaryServicing[2] && (
              <Grid.Col
                span="30%"
                css={{
                  '&.abyss-grid-col': {
                    marginBottom: '20px',
                    marginTop: '-35px',
                  },
                }}
              >
                <div>
                  <br />
                  <br />
                  <SecondaryServicing index={2} address={address} />
                </div>
              </Grid.Col>
            )}
        </Grid>
        <Grid>
          {address &&
            address.secondaryServicing &&
            address.secondaryServicing[3] && (
              <Grid.Col
                span="30%"
                css={{
                  '&.abyss-grid-col': {
                    marginBottom: '20px',
                    marginTop: '-35px',
                  },
                }}
              >
                <div>
                  <br />
                  <SecondaryServicing index={3} address={address} />
                </div>
              </Grid.Col>
            )}

          {address &&
            address.secondaryServicing &&
            address.secondaryServicing[4] && (
              <Grid.Col
                span="30%"
                css={{
                  '&.abyss-grid-col': {
                    marginBottom: '20px',
                    marginTop: '-35px',
                  },
                }}
              >
                <div>
                  <br />
                  <SecondaryServicing index={4} address={address} />
                </div>
              </Grid.Col>
            )}
        </Grid>
      </Card.Section>
    </Card>
  );
};
